import { IonContent, IonPage } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CommonHeader from "../../../CommonHeader";
import clevertapLogger from "../../../clevertapLogger";
import assistedMedicalConciergeImg from "../../../assets/images/assisted-medical-concierge.png";
import useUnhidePageOnEnter from "../../../hooks/useUnhidePageOnEnter";
import { markRestrictedSession } from "../../../utils/restrictedSession";

// localStorage key read by the chat input to auto-populate the message box.
export const CHAT_PREFILL_KEY = "chat_prefill_message";

const ICON_COLOR = "#97144D";

// Outlined option icons, drawn on a 24x24 grid to match the option row height.
const StethoscopeIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={ICON_COLOR}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h0" />
    <path d="M8 15v1a6 6 0 0 0 6 6a6 6 0 0 0 6-6v-4" />
    <circle cx="20" cy="10" r="2" />
  </svg>
);

const CalendarCheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={ICON_COLOR}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M8 2v4M16 2v4M3 10h18" />
    <path d="m9 16 2 2 4-4" />
  </svg>
);

const MessagesIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={ICON_COLOR}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
    <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
  </svg>
);

const ClipboardHeartIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={ICON_COLOR}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <g transform="translate(7.2 9.7) scale(0.4)">
      <path
        d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
        fill={ICON_COLOR}
        strokeWidth="4.5"
      />
    </g>
  </svg>
);

const OPTIONS = [
  {
    id: "doctor-recommendations",
    title: "Doctor & specialist recommendations",
    Icon: StethoscopeIcon,
    prefill:
      "Hi, I'm looking for personalised assistance with doctor and specialist recommendations. Could you guide me?",
  },
  {
    id: "appointment-bookings",
    title: "Appointment & test bookings",
    Icon: CalendarCheckIcon,
    prefill:
      "Hi, I'm looking for personalised assistance with appointment and test bookings. Could you guide me?",
  },
  {
    id: "second-opinion",
    title: "Second opinion coordination",
    Icon: MessagesIcon,
    prefill:
      "Hi, I'm looking for personalised assistance with second opinion coordination. Could you guide me?",
  },
  {
    id: "care-management",
    title: "Care Management Program details",
    Icon: ClipboardHeartIcon,
    prefill:
      "Hi, I'm looking for personalised assistance with the Care Management Program. Could you guide me?",
  },
];

const ChevronRight = ({ color = "#97144D" }) => (
  <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
    <path
      d="M1 1L8 8L1 15"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendArrow = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12H19M19 12L12 5M19 12L12 19"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function AssistedMedicalConcierge() {
  // Ionic can leave this page `ion-page-hidden` on re-entry, blanking it.
  const pageRef = useUnhidePageOnEnter();
  const history = useHistory();
  const [isPB, setIsPB] = useState(false);

  useEffect(() => {
    const source = localStorage.getItem("app_source");
    setIsPB(source === "pb");
    clevertapLogger.push("open_assisted_medical_concierge");
  }, []);

  const goToChat = (prefill) => {
    if (prefill) {
      localStorage.setItem(CHAT_PREFILL_KEY, prefill);
    } else {
      localStorage.removeItem(CHAT_PREFILL_KEY);
    }
    // AMC renders no tab bar, but /tab/chat does — mark the session before
    // going there so the patient cannot navigate on from the chat screen.
    markRestrictedSession();
    history.push("/tab/chat");
  };

  return (
    <IonPage ref={pageRef}>
      <IonContent>
        {isPB && <CommonHeader />}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            padding: "20px",
            boxSizing: "border-box",
            backgroundColor: "#FFFFFF",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <p
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "18px",
                fontWeight: 700,
                color: "#12194C",
                letterSpacing: "-0.2px",
                margin: 0,
              }}
            >
              Assisted medical concierge
            </p>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <img
              src={assistedMedicalConciergeImg}
              alt="Assisted medical concierge"
              style={{ width: "100%", maxWidth: "320px", height: "auto" }}
            />
          </div>

          <h2
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "24px",
              color: "#1A1A1A",
              margin: 0,
              marginBottom: "8px",
            }}
          >
            Not sure where to start? We'll coordinate it for you.
          </h2>

          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "21px",
              color: "#6B7280",
              margin: 0,
              marginBottom: "24px",
            }}
          >
            From finding the right doctor to booking appointments and arranging
            second opinions - get expert support every step of the way. We can
            help with:
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => goToChat(option.prefill)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  width: "100%",
                  padding: "18px 16px",
                  textAlign: "left",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  cursor: "pointer",
                  boxShadow: "0px 1px 3px rgba(18, 25, 76, 0.06)",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <option.Icon />
                </span>
                <span
                  style={{
                    flex: 1,
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#12194C",
                  }}
                >
                  {option.title}
                </span>
                <ChevronRight />
              </button>
            ))}
          </div>

          <div style={{ flex: 1, minHeight: "24px" }} />

          <button
            onClick={() => goToChat(null)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              width: "100%",
              padding: "16px",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              color: "#FFFFFF",
              backgroundColor: "#97144D",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              marginTop: "24px",
            }}
          >
            Go to chat
            <SendArrow />
          </button>
        </div>
      </IonContent>
    </IonPage>
  );
}
